import clsx from "clsx";
import type { CampaignCategoryBreakdown } from "../../../types/crm";

interface Props {
  breakdown: CampaignCategoryBreakdown | null;
}

/**
 * Compact variant — just category + bookings + value, suitable for the
 * 3rd column of the top row on the 360 page where we can't afford the
 * 9-column wide table. All categories listed vertically with a totals row.
 */
export function CampaignSummaryCompact({ breakdown }: Props) {
  if (!breakdown || breakdown.categories.length === 0) {
    return (
      <section className="bg-white rounded-xl border border-border-secondary shadow-sm h-full flex flex-col">
        <div className="px-4 py-3 border-b border-border-secondary">
          <h2 className="text-sm font-semibold text-content-primary">
            Campaigns
          </h2>
          <p className="text-[10px] uppercase tracking-wider text-content-tertiary mt-0.5">
            Google Ads · last 30d
          </p>
        </div>
        <div className="flex-1 p-4 text-xs text-content-tertiary italic">
          No breakdown available.
        </div>
      </section>
    );
  }

  const totals = breakdown.categories.reduce(
    (acc, c) => {
      acc.pur += c.pur;
      acc.purchaseValue += c.purchaseValue;
      return acc;
    },
    { pur: 0, purchaseValue: 0 },
  );

  return (
    <section className="bg-white rounded-xl border border-border-secondary shadow-sm h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-secondary">
        <div>
          <h2 className="text-sm font-semibold text-content-primary">
            Campaigns
          </h2>
          <p className="text-[10px] uppercase tracking-wider text-content-tertiary mt-0.5">
            Google Ads · last 30d
          </p>
        </div>
        <span className="text-[10px] text-content-tertiary tabular-nums">
          {breakdown.categories.length} categories
        </span>
      </div>
      <div className="flex-1 min-h-0 overflow-auto">
        <ul className="divide-y divide-border-secondary">
          {breakdown.categories.map((c) => {
            const bookings = Math.round(c.pur);
            const value = c.purchaseValue;
            const spent = bookings > 0 || value > 0;
            return (
              <li
                key={c.key}
                className={clsx(
                  "flex items-center justify-between gap-3 px-4 py-2.5",
                  !spent && "opacity-60",
                )}
              >
                <span className="text-xs text-content-primary font-medium truncate">
                  {c.label}
                </span>
                <span className="text-xs text-content-tertiary tabular-nums whitespace-nowrap">
                  {bookings}
                  <span className="text-content-tertiary/50"> · </span>
                  <span
                    className={clsx(
                      value > 0 ? "text-emerald-700" : "text-content-tertiary",
                    )}
                  >
                    {new Intl.NumberFormat("en-US", {
                      style: "currency",
                      currency: "USD",
                      maximumFractionDigits: 0,
                    }).format(value)}
                  </span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-border-secondary bg-gray-50/60">
        <span className="text-[11px] uppercase tracking-wider text-content-tertiary font-semibold">
          Total
        </span>
        <span className="text-xs tabular-nums font-semibold text-content-primary">
          {Math.round(totals.pur)} bookings ·{" "}
          {new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
            maximumFractionDigits: 0,
          }).format(totals.purchaseValue)}
        </span>
      </div>
    </section>
  );
}

function formatInt(n: number): string {
  return Math.round(n).toLocaleString();
}

function formatCurrency(n: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);
}

function formatPercent(n: number): string {
  // Values are already percent-scaled (e.g., 28.76 means 29%).
  return `${Math.round(n)}%`;
}

function formatDecimal(n: number): string {
  return String(Math.round(n));
}

/**
 * Per-category Google Ads campaign summary for the 360 page Performance
 * tab. Styling mirrors the hotel-facing /marketing `ReusableTable` look —
 * dashed row separators, dashed header/footer borders, gray body text with
 * a bold totals row — so the internal CS view matches what the customer sees.
 */
export function CampaignSummaryTable({ breakdown }: Props) {
  if (!breakdown || breakdown.categories.length === 0) {
    return (
      <div className="px-6 py-6 text-sm text-content-tertiary italic">
        No campaign breakdown available — click Refresh live above, or wait for
        the next 4x/day cron.
      </div>
    );
  }

  const totals = breakdown.categories.reduce(
    (acc, c) => {
      acc.impressions += c.impressions;
      acc.clicks += c.clicks;
      acc.atc += c.atc;
      acc.bco += c.bco;
      acc.pur += c.pur;
      acc.purchaseValue += c.purchaseValue;
      return acc;
    },
    { impressions: 0, clicks: 0, atc: 0, bco: 0, pur: 0, purchaseValue: 0 },
  );
  const overallCtr =
    totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
  const overallCvr =
    totals.clicks > 0 ? (totals.pur / totals.clicks) * 100 : 0;

  const numericCellCls =
    "py-2.5 px-3 text-right text-sm tabular-nums whitespace-nowrap";
  const headerCellCls =
    "py-2.5 px-3 text-xs font-semibold text-content-primary whitespace-nowrap";

  return (
    <div className="px-4 pb-4 overflow-x-auto">
      <table className="w-full border-collapse">
        <thead className="border-b border-dashed border-content-primary">
          <tr>
            <th className={clsx(headerCellCls, "text-left w-[22%]")}>Campaign Category</th>
            <th className={clsx(headerCellCls, "text-right")}>Impressions</th>
            <th className={clsx(headerCellCls, "text-right")}>Clicks</th>
            <th className={clsx(headerCellCls, "text-right")}>CTR</th>
            <th className={clsx(headerCellCls, "text-right")}>ATC</th>
            <th className={clsx(headerCellCls, "text-right")}>BCO</th>
            <th className={clsx(headerCellCls, "text-right")}>Bookings (#)</th>
            <th className={clsx(headerCellCls, "text-right")}>Bookings ($)</th>
            <th className={clsx(headerCellCls, "text-right")}>CVR</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-dashed divide-[#D8D8D8]">
          {breakdown.categories.map((c) => (
            <tr
              key={c.key}
              className="hover:bg-black/[0.01] hover:shadow-[0_2px_12px_rgba(0,0,0,0.08)] transition-[background-color,box-shadow] duration-200"
            >
              <td className="py-2.5 px-3 text-sm font-medium text-content-primary whitespace-nowrap">
                {c.label}
              </td>
              <td className={clsx(numericCellCls, "text-stone-400")}>
                {formatInt(c.impressions)}
              </td>
              <td className={clsx(numericCellCls, "text-stone-400")}>
                {formatInt(c.clicks)}
              </td>
              <td className={clsx(numericCellCls, "text-stone-400")}>
                {formatPercent(c.ctr)}
              </td>
              <td className={clsx(numericCellCls, "text-stone-400")}>
                {formatDecimal(c.atc)}
              </td>
              <td className={clsx(numericCellCls, "text-stone-400")}>
                {formatDecimal(c.bco)}
              </td>
              <td className={clsx(numericCellCls, "text-stone-400")}>
                {formatDecimal(c.pur)}
              </td>
              <td className={clsx(numericCellCls, "text-stone-400")}>
                {formatCurrency(c.purchaseValue)}
              </td>
              <td className={clsx(numericCellCls, "text-stone-400")}>
                {formatPercent(c.cvr)}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot className="border-t border-dashed border-content-primary">
          <tr>
            <td className="py-2.5 px-3 text-sm font-semibold text-content-primary whitespace-nowrap">
              Total
            </td>
            <td className={clsx(numericCellCls, "font-semibold text-content-primary")}>
              {formatInt(totals.impressions)}
            </td>
            <td className={clsx(numericCellCls, "font-semibold text-content-primary")}>
              {formatInt(totals.clicks)}
            </td>
            <td className={clsx(numericCellCls, "font-semibold text-content-primary")}>
              {formatPercent(overallCtr)}
            </td>
            <td className={clsx(numericCellCls, "font-semibold text-content-primary")}>
              {formatDecimal(totals.atc)}
            </td>
            <td className={clsx(numericCellCls, "font-semibold text-content-primary")}>
              {formatDecimal(totals.bco)}
            </td>
            <td className={clsx(numericCellCls, "font-semibold text-content-primary")}>
              {formatDecimal(totals.pur)}
            </td>
            <td className={clsx(numericCellCls, "font-semibold text-content-primary")}>
              {formatCurrency(totals.purchaseValue)}
            </td>
            <td className={clsx(numericCellCls, "font-semibold text-content-primary")}>
              {formatPercent(overallCvr)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
